import { useTranslation } from 'react-i18next';

import { Body1, Flex, Stack } from '@myphx-engineering/phx-design-system';

import { useMedicationConfigOverride } from '../../../../../contexts/DrugProgramProvider';
import type {
    AuthorizedMedicationInfoFragment,
    MailOrderProviderInfoFragment,
    PharmacyProviderInfoFragment,
    UnknownProviderInfoFragment,
} from '../../../../../graphql/generated/graphql';
import { getProviderName } from '../../../../../util';
import { PharmacyChangeLink } from '../PharmacyChangeLink';

import { ProviderOffersCard } from './ProviderOffersCard';

export const ProviderOffersCardList = ({
    providers,
    medication,
    onSelect,
    drxId,
}: {
    providers: (
        | MailOrderProviderInfoFragment
        | PharmacyProviderInfoFragment
        | UnknownProviderInfoFragment
    )[];
    medication: AuthorizedMedicationInfoFragment;
    onSelect: (id: string, providerId?: string) => void;
    drxId: string;
}) => {
    const { t } = useTranslation();
    const configOverride = useMedicationConfigOverride();
    const { pharmacyOverrides } = configOverride;

    const sortedProviders = providers.toSorted((a, b) => {
        const providerNameA = getProviderName(a, pharmacyOverrides[a.id]);
        const providerNameB = getProviderName(b, pharmacyOverrides[b.id]);

        return providerNameA.localeCompare(providerNameB);
    });

    return (
        <Stack gap="md">
            <Flex justify="space-between" align="center">
                <Body1>
                    {t('providerOffers.pharmacies', {
                        count: providers?.length,
                    })}
                </Body1>
            </Flex>

            <Stack gap="sm" role="list">
                {sortedProviders.map((prov) => {
                    return (
                        <ProviderOffersCard
                            key={`card-${prov.id}`}
                            provider={prov}
                            medication={medication}
                            onSelected={onSelect}
                        />
                    );
                })}
            </Stack>

            <PharmacyChangeLink drxId={drxId} />
        </Stack>
    );
};
